import React, { useState, useEffect, useMemo, useCallback } from 'react';
import DateInput from './DateInput';
import { callAxiosGet } from '../tmslib/src/util/axiosutils';
import { useMessageState } from '../tmslib/src/context/MessageContext';
import { getMainAxios } from '../utils/util';
import { useAuth } from '../context/AuthContext';

/**
 * 매매일지 — 당일 매매 손익·시황을 게시판처럼 최근 N일 나열한다.
 *
 * 계좌는 상단 전역 선택(useAuth().selectedPfId)을 따른다. 화면 자체 계좌 select 는 두지 않는다.
 * 신규 API 없이 기존 엔드포인트만 쓴다.
 *   /api/v1/portfolio/daily-return  일별 수익률 (NAV·일별/누적 수익률)
 *   /api/v1/portfolio/daily-detail  당일 포지션 + 체결 + 수수료/세금
 *
 * daily-detail 은 일자별 호출이라, 목록에 보이는 일자만 병렬 조회한다.
 */

interface DailyRow {
  d: string;
  na: number;
  prevNa: number;
  dailyRet: number;
  cumRet: number;
  diffAmt: number;
  fee?: number;
  tax?: number;
}

interface PositionRow {
  prodId: string;
  prodNm?: string;
  pos: number;
  avgPrc: number;
  c: number;
  prft: number;      // 평가손익
  evluRt: number;    // 평가손익률
  prftKrw: number;   // 실현손익
  fee: number;
  tax: number;
}

interface FillRow {
  ordT: string;
  ordNo?: string;
  prodId: string;
  prodNm?: string;
  lsNm?: string;
  totFillQty: number;
  avgPrc: number;
  totFillAmt: number;
}

interface DetailRes {
  d: string;
  positions: PositionRow[];
  fills: FillRow[];
  feeSum: number;
  taxSum: number;
}

/** 일자별 카드에 표시할 집계 */
interface JournalEntry {
  d: string;
  daily?: DailyRow;
  detail?: DetailRes;
  realized: number;
  unrealized: number;
  fee: number;
  tax: number;
  net: number;
  fillCnt: number;
  buyAmt: number;
  sellAmt: number;
  wins: number;
  closedCnt: number;
  winRate: number | null;
  best?: PositionRow;
  worst?: PositionRow;
}

// ── 기존 화면(PortfolioReview) 과 동일한 스타일 규칙 ──────────
const card: React.CSSProperties = {
  backgroundColor: 'white', border: '2px solid #3b82f6',
  borderRadius: '8px', padding: '12px 16px', marginBottom: '16px',
};
const gridTitle: React.CSSProperties = {
  margin: 0, fontSize: '12px', fontWeight: 600, color: '#1e40af',
};
const gridHint: React.CSSProperties = {
  marginLeft: 6, fontSize: '11px', fontWeight: 400, color: '#9ca3af',
};
const titleRow: React.CSSProperties = {
  display: 'flex', justifyContent: 'space-between',
  alignItems: 'center', marginBottom: '12px',
};
const sign = (v?: number) =>
  (v ?? 0) > 0 ? '#dc2626' : (v ?? 0) < 0 ? '#2563eb' : '#374151';

const won = (v?: number | null) => {
  if (v == null) return '-';
  // Math.round(-0.4) === -0 이라 그대로 찍으면 "-0" 이 된다.
  const n = Math.round(v);
  return (n === 0 ? 0 : n).toLocaleString('ko-KR');
};
/** 비용(수수료·세금)처럼 "빼는 값"은 0 일 때 부호를 붙이지 않는다 */
const cost = (v?: number | null) => {
  const n = Math.round(v ?? 0);
  return n === 0 ? '0' : (-n).toLocaleString('ko-KR');
};
const pct = (v?: number | null, digits = 2) =>
  v == null ? '-' : `${v >= 0 ? '+' : ''}${v.toFixed(digits)}%`;

const DEFAULT_DAYS = 10;

const TradingJournal: React.FC = () => {
  const { msgBox: m, logger } = useMessageState();
  const axiosInstance = getMainAxios();

  const { selectedPfId } = useAuth();
  const pfId = selectedPfId;
  const [days, setDays] = useState(DEFAULT_DAYS);
  const [endD, setEndD] = useState(new Date().toISOString().split('T')[0]);
  const [entries, setEntries] = useState<JournalEntry[]>([]);
  const [openD, setOpenD] = useState<string | null>(null);   // 펼친 일자
  const [loading, setLoading] = useState(false);

  const buildEntry = (
    d: string, daily?: DailyRow, detail?: DetailRes,
  ): JournalEntry => {
    const ps = detail?.positions ?? [];
    const fs = detail?.fills ?? [];
    const realized = ps.reduce((a, p) => a + (p.prftKrw || 0), 0);
    const unrealized = ps.reduce((a, p) => a + (p.prft || 0), 0);
    const fee = detail?.feeSum ?? 0;
    const tax = detail?.taxSum ?? 0;
    const buys = fs.filter(f => (f.lsNm || '').includes('매수'));
    const sells = fs.filter(f => (f.lsNm || '').includes('매도'));
    const closed = ps.filter(p => (p.prftKrw || 0) !== 0);
    const wins = closed.filter(p => p.prftKrw > 0).length;
    const ranked = [...ps]
      .filter(p => (p.prft || 0) !== 0 || (p.prftKrw || 0) !== 0)
      .sort((a, b) => ((b.prft || 0) + (b.prftKrw || 0)) - ((a.prft || 0) + (a.prftKrw || 0)));

    return {
      d, daily, detail,
      realized, unrealized, fee, tax,
      // 순손익 — kispfpos.prftKrw(실현손익)가 null 인 계좌가 있어
      // realized 기준으로 잡으면 0 이 된다. NAV 증감(diffAmt)이 그날의 실제 손익이다.
      net: daily?.diffAmt ?? (realized - fee - tax),
      fillCnt: fs.length,
      buyAmt: buys.reduce((a, f) => a + (f.totFillAmt || 0), 0),
      sellAmt: sells.reduce((a, f) => a + (f.totFillAmt || 0), 0),
      wins, closedCnt: closed.length,
      winRate: closed.length ? (wins / closed.length) * 100 : null,
      best: ranked[0],
      worst: ranked.length > 1 ? ranked[ranked.length - 1] : undefined,
    };
  };

  const fetchAll = useCallback(() => {
    if (!pfId) return;
    setLoading(true);
    setOpenD(null);

    callAxiosGet({
      axiosInstance, m, logger,
      url: '/api/v1/portfolio/daily-return',
      params: { id: pfId },
      onSuccess: async (data) => {
        // daily-return 은 배열이 아니라 { summary, rows } 객체를 반환한다.
        // 배열로 다루면 항상 빈 목록이 되어 "매매기록이 없습니다"만 나온다.
        const res = data as { rows?: DailyRow[] } | DailyRow[] | null;
        const all: DailyRow[] = Array.isArray(res) ? res : (res?.rows ?? []);
        const rows = all
          .filter(r => r.d <= endD)
          .sort((a, b) => (a.d < b.d ? 1 : -1))   // 최신순
          .slice(0, days);

        if (rows.length === 0) { setEntries([]); setLoading(false); return; }

        // 각 일자의 상세를 병렬 조회 (실패한 일자는 daily 만으로 카드 구성)
        const details = await Promise.all(rows.map(r =>
          axiosInstance
            .get('/api/v1/portfolio/daily-detail', { params: { id: pfId, d: r.d } })
            .then(res => res.data as DetailRes)
            .catch(() => undefined),
        ));

        setEntries(rows.map((r, i) => buildEntry(r.d, r, details[i])));
        setLoading(false);
      },
      onError: () => { setEntries([]); setLoading(false); },
    });
  }, [axiosInstance, m, logger, pfId, days, endD]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const totNet = useMemo(() => entries.reduce((a, e) => a + e.net, 0), [entries]);

  return (
    <div style={{ padding: '20px', backgroundColor: '#eff6ff', minHeight: '100vh' }}>
      {/* 조회 조건 */}
      <div style={{ ...card, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 10 }}>
        <span style={{ color: '#1e40af', fontSize: '12px', fontWeight: 600 }}>
          매매일지 {loading ? '(조회 중...)' : `— 최근 ${entries.length}일`}
          {!loading && entries.length > 0 && (
            <span style={{ marginLeft: 8, color: sign(totNet), fontWeight: 600 }}>
              합계 {won(totNet)}원
            </span>
          )}
        </span>
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
          <select
            value={days}
            onChange={e => setDays(Number(e.target.value))}
            style={{ fontSize: '12px', padding: '4px 8px', border: '1px solid #d1d5db', borderRadius: '3px' }}
          >
            {[10, 20, 30, 60].map(n => <option key={n} value={n}>최근 {n}일</option>)}
          </select>
          <DateInput value={endD} onChange={setEndD} style={{ fontSize: '12px' }} />
          <button onClick={fetchAll}
            style={{ padding: '5px 12px', backgroundColor: '#3b82f6', color: 'white', border: 'none', borderRadius: '3px', cursor: 'pointer', fontSize: '12px', fontWeight: 500 }}>
            조회
          </button>
        </div>
      </div>

      {/* 게시판 — 일자별 카드 */}
      <div style={card}>
        <div style={titleRow}>
          <h3 style={gridTitle}>
            일별 매매기록 <span style={gridHint}>(클릭: 체결 내역 펼치기)</span>
          </h3>
          <span style={{ color: '#1e40af', fontSize: '12px', fontWeight: 500 }}>
            총 {entries.length}건
          </span>
        </div>

        {!loading && entries.length === 0 && (
          <p style={{ fontSize: '12px', color: '#9ca3af', margin: '8px 0' }}>
            해당 기간 매매기록이 없습니다.
          </p>
        )}

        {entries.map(e => {
          const open = openD === e.d;
          const traded = e.fillCnt > 0;
          return (
            <div key={e.d} style={{
              borderTop: '1px solid #e5e7eb',
              borderLeft: `3px solid ${traded ? sign(e.net) : '#e5e7eb'}`,
              paddingLeft: 10,
            }}>
              {/* 제목 행 */}
              <div
                onClick={() => setOpenD(open ? null : e.d)}
                style={{
                  display: 'flex', alignItems: 'baseline', gap: 10,
                  padding: '8px 0', cursor: 'pointer', fontSize: '12px',
                  flexWrap: 'wrap',
                }}
              >
                <span style={{ color: '#9ca3af', width: 12 }}>{open ? '▾' : '▸'}</span>
                <b style={{ color: '#1e40af', width: 88 }}>{e.d}</b>

                <b style={{ color: sign(e.net), width: 108, textAlign: 'right' }}>
                  {won(e.net)}원
                </b>
                {e.daily && (
                  <span style={{ color: sign(e.daily.dailyRet), width: 64, textAlign: 'right' }}>
                    {pct(e.daily.dailyRet)}
                  </span>
                )}

                {/* 제목 옆 요약 — 게시판 미리보기 */}
                <span style={{ color: '#4b5563', flex: 1, minWidth: 220 }}>
                  {traded ? (
                    <>
                      체결 {e.fillCnt}건
                      {e.buyAmt > 0 && ` · 매수 ${won(e.buyAmt)}`}
                      {e.sellAmt > 0 && ` · 매도 ${won(e.sellAmt)}`}
                      {e.winRate != null && ` · 승률 ${e.winRate.toFixed(0)}%`}
                      {e.best && (
                        <span style={{ color: '#9ca3af' }}>
                          {' · '}주도 {e.best.prodNm || e.best.prodId}
                        </span>
                      )}
                    </>
                  ) : (
                    <span style={{ color: '#9ca3af' }}>매매 없음 (보유 평가만)</span>
                  )}
                </span>

                {e.daily && (
                  <span style={{ color: '#6b7280', fontSize: '11px' }}>
                    NAV {won(e.daily.na)}
                  </span>
                )}
              </div>

              {/* 펼친 내용 */}
              {open && (
                <div style={{ padding: '4px 0 12px 22px' }}>
                  {/* 지표 */}
                  <div style={{
                    display: 'flex', gap: 16, flexWrap: 'wrap',
                    fontSize: '12px', marginBottom: 10,
                  }}>
                    {[
                      { t: '실현손익', v: e.realized },
                      { t: '평가손익', v: e.unrealized },
                      { t: '수수료', v: -e.fee, isCost: true },
                      { t: '세금', v: -e.tax, isCost: true },
                      { t: '순손익', v: e.net },
                    ].map(x => (
                      <span key={x.t} style={{ color: '#6b7280' }}>
                        {x.t} <b style={{ color: sign(x.v) }}>
                          {(x as any).isCost ? cost(x.v) : won(x.v)}
                        </b>
                      </span>
                    ))}
                  </div>

                  {/* 손익 기여 */}
                  {(e.best || e.worst) && (
                    <div style={{ fontSize: '12px', marginBottom: 10 }}>
                      {e.best && (
                        <span style={{ marginRight: 16 }}>
                          <span style={{ color: '#6b7280' }}>수익 </span>
                          <b>{e.best.prodNm || e.best.prodId}</b>{' '}
                          <b style={{ color: sign((e.best.prft || 0) + (e.best.prftKrw || 0)) }}>
                            {won((e.best.prft || 0) + (e.best.prftKrw || 0))}원
                          </b>
                          <span style={{ color: '#9ca3af' }}> {pct(e.best.evluRt)}</span>
                        </span>
                      )}
                      {e.worst && (
                        <span>
                          <span style={{ color: '#6b7280' }}>손실 </span>
                          <b>{e.worst.prodNm || e.worst.prodId}</b>{' '}
                          <b style={{ color: sign((e.worst.prft || 0) + (e.worst.prftKrw || 0)) }}>
                            {won((e.worst.prft || 0) + (e.worst.prftKrw || 0))}원
                          </b>
                          <span style={{ color: '#9ca3af' }}> {pct(e.worst.evluRt)}</span>
                        </span>
                      )}
                    </div>
                  )}

                  {/* 체결 타임라인 */}
                  {e.detail && e.detail.fills.length > 0 ? (
                    e.detail.fills.map((f, i) => {
                      const isBuy = (f.lsNm || '').includes('매수');
                      return (
                        <div key={`${f.ordNo}-${i}`} style={{
                          display: 'flex', gap: 10, alignItems: 'baseline',
                          padding: '4px 0', borderTop: '1px solid #f3f4f6', fontSize: '12px',
                        }}>
                          <span style={{ color: '#9ca3af', width: 60, flexShrink: 0 }}>{f.ordT}</span>
                          <span style={{
                            width: 32, flexShrink: 0, textAlign: 'center', borderRadius: 3,
                            fontSize: '11px', fontWeight: 600,
                            color: isBuy ? '#dc2626' : '#2563eb',
                            backgroundColor: isBuy ? '#fee2e2' : '#dbeafe',
                          }}>
                            {isBuy ? '매수' : '매도'}
                          </span>
                          <span style={{ flex: 1 }}>
                            <b>{f.prodNm || f.prodId}</b>
                            <span style={{ color: '#9ca3af', marginLeft: 6 }}>{f.prodId}</span>
                          </span>
                          <span style={{ color: '#4b5563' }}>
                            {won(f.avgPrc)}원 × {f.totFillQty}
                          </span>
                          <span style={{ fontWeight: 600, width: 92, textAlign: 'right' }}>
                            {won(f.totFillAmt)}원
                          </span>
                        </div>
                      );
                    })
                  ) : (
                    <p style={{ fontSize: '12px', color: '#9ca3af', margin: 0 }}>체결 없음</p>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default TradingJournal;
